import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Row, Col, Card, CardBody, Button, Badge, Spinner, Alert, Input, InputGroup, InputGroupText,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
} from 'reactstrap';
import {
  FaCheck, FaTimes, FaEye, FaSearch, FaSyncAlt, FaPlay, FaClock, FaPhone, FaBuilding,
  FaTag, FaUser, FaHourglassHalf, FaInbox, FaCheckCircle, FaExclamationTriangle, FaCalendarAlt,
} from 'react-icons/fa';
import ReelDetailsModal, { ReelThumb, secureUrl, formatDateTime, API_BASE_URL } from './ReelDetailsModal';

// Replace with your real logged-in admin id (e.g. from auth context)
const ADMIN_ID = localStorage.getItem('adminId') || '6821a4d62775dec5bb14f9fe';
const PAGE_SIZE = 9;

const PendingReels = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // which action is running: { id, type }
  const [busy, setBusy] = useState(null);

  // Video preview modal
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsReelId, setDetailsReelId] = useState(null);

  // Reject modal
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/reels/pending`);
      setReels(res.data.reels || res.data.data || []);
    } catch (err) {
      console.error('Error fetching pending reels:', err);
      setError(err.response?.data?.message || 'Failed to fetch pending reels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const getCategoryName = (reel) =>
    reel.reelCategory?.name ||
    (typeof reel.reelCategoryId === 'object' && reel.reelCategoryId?.name) ||
    'N/A';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = reels.filter((r) => {
      if (!q) return true;
      return (
        (r.businessName || '').toLowerCase().includes(q) ||
        (r.phoneNumber || '').includes(q) ||
        (r.creatorId?.name || '').toLowerCase().includes(q) ||
        getCategoryName(r).toLowerCase().includes(q)
      );
    });
    list = [...list].sort((a, b) => {
      const diff = new Date(b.createdAt) - new Date(a.createdAt);
      return sort === 'newest' ? diff : -diff;
    });
    return list;
  }, [reels, search, sort]);

  const visible = filtered.slice(0, visibleCount);

  const removeFromList = (id) => setReels((prev) => prev.filter((r) => r._id !== id));

  const handleApprove = async (reel) => {
    setBusy({ id: reel._id, type: 'approve' });
    setError(null);
    try {
      await axios.put(`${API_BASE_URL}/reels/${reel._id}/approve-reject`, {
        action: 'approve',
        adminId: ADMIN_ID,
      });
      removeFromList(reel._id);
      flash(`"${reel.businessName || 'Reel'}" approved successfully!`);
    } catch (err) {
      console.error('Approve error:', err);
      setError(err.response?.data?.message || 'Failed to approve reel');
    } finally {
      setBusy(null);
    }
  };

  const openReject = (reel) => {
    setRejectTarget(reel);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const reel = rejectTarget;
    setBusy({ id: reel._id, type: 'reject' });
    setError(null);
    try {
      const payload = { action: 'reject', adminId: ADMIN_ID };
      if (rejectReason.trim()) payload.rejectedReason = rejectReason.trim();
      await axios.put(`${API_BASE_URL}/reels/${reel._id}/approve-reject`, payload);
      removeFromList(reel._id);
      setRejectTarget(null);
      flash(`"${reel.businessName || 'Reel'}" rejected.`);
    } catch (err) {
      console.error('Reject error:', err);
      setError(err.response?.data?.message || 'Failed to reject reel');
    } finally {
      setBusy(null);
    }
  };

  const openVideo = (url) => {
    setSelectedVideo(secureUrl(url));
    setVideoModal(true);
  };

  const openDetails = (reel) => {
    setDetailsReelId(reel._id);
    setDetailsOpen(true);
  };

  const isBusy = (id, type) => busy && busy.id === id && (!type || busy.type === type);

  return (
    <div className="container-fluid px-3 px-md-4 py-4">
      <style>{`
        .pr-card{transition:transform .2s, box-shadow .2s;border-radius:14px;overflow:hidden}
        .pr-card:hover{transform:translateY(-4px);box-shadow:0 10px 24px rgba(0,0,0,.12)!important}
        .pr-meta{font-size:.85rem}
        .pr-fade{animation:prFade .3s ease}
        @keyframes prFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        .pr-actions .btn{transition:transform .15s}
        .pr-actions .btn:hover:not(:disabled){transform:translateY(-2px)}
      `}</style>

      {/* Header */}
      <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-4">
        <div>
          <h4 className="text-primary fw-bold mb-1">
            <FaHourglassHalf className="me-2" /> Pending Reels
          </h4>
          <p className="text-muted mb-0">Review user-submitted reels and approve or reject them</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge color="warning" className="p-2 px-3 text-dark">
            {reels.length} awaiting review
          </Badge>
          <Button color="primary" outline size="sm" onClick={fetchPending} disabled={loading}>
            <FaSyncAlt className={loading ? 'me-1 fa-spin' : 'me-1'} /> Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="danger" className="border-0 shadow-sm" toggle={() => setError(null)}>
          <FaExclamationTriangle className="me-2" /> {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" className="border-0 shadow-sm" toggle={() => setSuccess(null)}>
          <FaCheckCircle className="me-2" /> {success}
        </Alert>
      )}

      {/* Toolbar */}
      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={8}>
              <InputGroup>
                <InputGroupText className="bg-white"><FaSearch className="text-muted" /></InputGroupText>
                <Input
                  placeholder="Search by business, phone, creator or category..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
                />
                {search && (
                  <Button color="light" onClick={() => setSearch('')}><FaTimes /></Button>
                )}
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Input type="select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </Input>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Content */}
      {loading && reels.length === 0 ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted mt-2">Loading pending reels...</p>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="text-center py-5 text-muted">
            <FaInbox size={48} className="mb-3 opacity-25" />
            <h6 className="fw-bold">{reels.length === 0 ? 'All caught up!' : 'No matching reels'}</h6>
            <p className="mb-0">
              {reels.length === 0 ? 'There are no reels waiting for approval.' : 'Try a different search term.'}
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          <Row className="g-4">
            {visible.map((reel) => (
              <Col xs={12} sm={6} xl={4} key={reel._id} className="pr-fade">
                <Card className="pr-card border-0 shadow-sm h-100">
                  <ReelThumb
                    reel={reel}
                    width="100%"
                    height={220}
                    radius={0}
                    onClick={() => openVideo(reel.videoUrl)}
                  />
                  <CardBody className="d-flex flex-column p-3">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <h6 className="fw-bold mb-0 text-truncate" title={reel.businessName}>
                        <FaBuilding className="text-primary me-2" />
                        {reel.businessName || 'Untitled'}
                      </h6>
                      <Badge color="warning" className="text-dark text-capitalize">pending</Badge>
                    </div>

                    <div className="pr-meta text-muted d-flex flex-column gap-1 mb-3">
                      <span><FaPhone className="me-2" />{reel.phoneNumber || 'N/A'}</span>
                      <span><FaTag className="me-2" />{getCategoryName(reel)}</span>
                      <span><FaClock className="me-2" />{reel.duration ?? 0}s</span>
                      <span><FaCalendarAlt className="me-2" />{formatDateTime(reel.createdAt)}</span>
                      <span className="text-truncate">
                        <FaUser className="me-2" />
                        {reel.creatorId?.name || 'Unknown'}
                        {reel.creatorId?.mobile ? ` • ${reel.creatorId.mobile}` : ''}
                      </span>
                    </div>

                    <div className="pr-actions mt-auto">
                      <Button
                        color="info"
                        outline
                        size="sm"
                        className="w-100 mb-2"
                        onClick={() => openDetails(reel)}
                      >
                        <FaEye className="me-2" /> View Details
                      </Button>
                      <div className="d-flex gap-2">
                        <Button
                          color="success"
                          className="flex-fill fw-bold"
                          disabled={!!busy}
                          onClick={() => handleApprove(reel)}
                        >
                          {isBusy(reel._id, 'approve') ? (
                            <Spinner size="sm" />
                          ) : (
                            <><FaCheck className="me-1" /> Approve</>
                          )}
                        </Button>
                        <Button
                          color="danger"
                          outline
                          className="flex-fill fw-bold"
                          disabled={!!busy}
                          onClick={() => openReject(reel)}
                        >
                          <FaTimes className="me-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-4">
            <small className="text-muted d-block mb-2">
              Showing {visible.length} of {filtered.length}
            </small>
            {visibleCount < filtered.length && (
              <Button color="primary" outline onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                Load more
              </Button>
            )}
          </div>
        </>
      )}

      {/* Video preview modal */}
      <Modal isOpen={videoModal} toggle={() => setVideoModal(false)} centered size="lg">
        <ModalHeader toggle={() => setVideoModal(false)} className="border-0 pb-0">
          <FaPlay className="text-primary me-2" size={16} />
          <span className="fw-bold">Reel Preview</span>
        </ModalHeader>
        <ModalBody className="text-center p-3">
          {selectedVideo && (
            <video
              src={selectedVideo}
              controls
              autoPlay
              playsInline
              className="w-100 rounded shadow-sm"
              style={{ maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </ModalBody>
        <ModalFooter className="border-0 pt-0">
          <Button color="secondary" onClick={() => setVideoModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Reject modal */}
      <Modal isOpen={!!rejectTarget} toggle={() => !busy && setRejectTarget(null)} centered>
        <ModalHeader toggle={() => !busy && setRejectTarget(null)} className="border-0 pb-0">
          <FaTimes className="text-danger me-2" />
          <span className="fw-bold">Reject Reel</span>
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            Reject <strong>{rejectTarget?.businessName || 'this reel'}</strong>? The creator will see it as rejected.
          </p>
          <FormGroup className="mb-0">
            <Label className="fw-bold text-secondary">Reason (optional)</Label>
            <Input
              type="textarea"
              rows={3}
              maxLength={200}
              placeholder="e.g. Inappropriate content, low video quality..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <small className="text-muted">{rejectReason.length}/200</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-0 pt-0">
          <Button color="secondary" onClick={() => setRejectTarget(null)} disabled={!!busy}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleReject} disabled={!!busy}>
            {busy?.type === 'reject' ? <Spinner size="sm" /> : <><FaTimes className="me-1" /> Confirm Reject</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Details modal */}
      <ReelDetailsModal
        isOpen={detailsOpen}
        toggle={() => setDetailsOpen(false)}
        reelId={detailsReelId}
      />
    </div>
  );
};

export default PendingReels;